import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { print } from 'graphql';

import { graphql } from '~/shared/codegen';
import { type FragmentType, useFragment } from '~/shared/codegen/fragment-masking';
import { fetchGraphQL } from '~/shared/helpers/fetch-graphql';
import type { Locale } from '~/universal/constants';

export const LIMIT_OF_POSTS = 6;
export const LIMIT_OF_TOP_RATED_CATEGORIES = 5;

export const PopularArticlesItemFragment = graphql(/* GraphQL */ `
  fragment PopularArticlesItemFragment on Post {
    _id
    title
    alias
    image {
      path
    }
  }
`);

export const TopRatedCategoriesItemFragment = graphql(/* GraphQL */ `
  fragment TopRatedCategoriesItemFragment on Category {
    _id
    name
    alias
  }
`);

export const BlogMenuQuery = graphql(/* GraphQL */ `
  query blogMenuQuery(
    $postsLimit: Int!
    $categoriesLimit: Int!
    $postsFilter: FilterFindManyPostInput
    $categoriesFilter: FilterFindManyCategoryInput
  ) {
    popularArticles: findManyPostQuery(filter: $postsFilter, skip: 0, limit: $postsLimit, sort: _ID_DESC) {
      ...PopularArticlesItemFragment
    }

    topRatedCategories: findManyCategoryQuery(
      filter: $categoriesFilter
      skip: 0
      limit: $categoriesLimit
      sort: _ID_ASC
    ) {
      ...TopRatedCategoriesItemFragment
    }
  }
`);

export type BlogMenuQueryData = ResultOf<typeof BlogMenuQuery>;
type BlogMenuQueryVariables = VariablesOf<typeof BlogMenuQuery>;

export type PopularArticleItemRef = FragmentType<typeof PopularArticlesItemFragment>;
export type TopRatedCategoryItemRef = FragmentType<typeof TopRatedCategoriesItemFragment>;

export type PopularArticleMenuItem = Readonly<{
  _id: string;
  item: PopularArticleItemRef;
}>;

export type TopRatedCategoryMenuItem = Readonly<{
  _id: string;
  item: TopRatedCategoryItemRef;
}>;

export type BlogMenuData = Readonly<{
  popularArticles: PopularArticleMenuItem[];
  topRatedCategories: TopRatedCategoryMenuItem[];
}>;

const blogMenuCache = new Map<string, BlogMenuData>();
const pendingRequests = new Map<string, Promise<BlogMenuData>>();

const mapPopularArticles = (items: NonNullable<BlogMenuQueryData['popularArticles']>): PopularArticleMenuItem[] => {
  return items.map((rawItem) => {
    const unwrappedItem = useFragment(PopularArticlesItemFragment, rawItem);

    return {
      _id: unwrappedItem._id,
      item: rawItem,
    };
  });
};

const mapTopRatedCategories = (
  items: NonNullable<BlogMenuQueryData['topRatedCategories']>,
): TopRatedCategoryMenuItem[] => {
  return items.map((rawItem) => {
    const unwrappedItem = useFragment(TopRatedCategoriesItemFragment, rawItem);

    return {
      _id: unwrappedItem._id,
      item: rawItem,
    };
  });
};

export const getCachedBlogMenu = (lng: Locale): BlogMenuData | null => {
  return blogMenuCache.get(lng) ?? null;
};

export const fetchBlogMenu = async (lng: Locale): Promise<BlogMenuData> => {
  const cachedData = blogMenuCache.get(lng);

  if (cachedData) {
    return cachedData;
  }

  const pendingRequest = pendingRequests.get(lng);

  if (pendingRequest) {
    return pendingRequest;
  }

  const request = fetchGraphQL<BlogMenuQueryData, BlogMenuQueryVariables>({
    query: print(BlogMenuQuery),
    lng,
    variables: {
      postsLimit: LIMIT_OF_POSTS,
      categoriesLimit: LIMIT_OF_TOP_RATED_CATEGORIES,
      postsFilter: {
        language: lng,
        isPublished: true,
      },
      categoriesFilter: {
        language: lng,
        isPublished: true,
      },
    },
  })
    .then((data) => {
      const nextData: BlogMenuData = {
        popularArticles: mapPopularArticles(data.popularArticles ?? []),
        topRatedCategories: mapTopRatedCategories(data.topRatedCategories ?? []),
      };

      blogMenuCache.set(lng, nextData);
      pendingRequests.delete(lng);

      return nextData;
    })
    .catch((error: unknown) => {
      pendingRequests.delete(lng);
      throw error;
    });

  pendingRequests.set(lng, request);

  return request;
};

export const resetBlogMenuCache = (): void => {
  blogMenuCache.clear();
  pendingRequests.clear();
};
